// Die Quelle für „läuft gerade" (Typ und Texte: laeuft.ts). Sammelt aus
// den Stores, die ohnehin laufen -- kein eigener Datenpfad. Reaktiv, weil
// die Stores SvelteMaps sind: aufgerufen in einem $derived folgt die
// Anzeige jeder Änderung von selbst.
//
// Neues Gerät: hier eine Zeile, die aus seinem Store einen Laeuft-Eintrag
// macht (oder keinen, wenn es ruht). Der Store muss beim Start laufen
// (App.svelte, startBootStreams).

import { argusLaeuft } from "../module/argus/argus";
import { getArgus } from "../module/argus/argus.svelte";
import { backupProbe } from "./backup-probe.svelte";
import { getClaude } from "./claude-summary.svelte";
import { claudeLaeuft } from "./claude/claude";
import { druckerLaeuft } from "./drucker/drucker";
import { getDrucker } from "./drucker-summary.svelte";
import type { Laeuft } from "./laeuft";
import { sectionForPath, type SectionId } from "./nav-sections";
import { probeLaeuft } from "./probe/probe";
import { erfahrungAus } from "./probe/gesamt";

export function laeuftGerade(): Laeuft[] {
  const out: Laeuft[] = [];
  const drucker = druckerLaeuft(getDrucker());
  if (drucker) out.push(drucker);
  // Die Wiederherstellungsprobe, solange sie Ketten zurückspielt
  // (docs/restore-probe.md).
  const probe = probeLaeuft(backupProbe.lauf, erfahrungAus(backupProbe.ketten));
  if (probe) out.push(probe);
  // Argus: ein Training auf RunPod (docs/argus-modul.md).
  const argus = argusLaeuft(getArgus());
  if (argus) out.push(argus);
  // Claude-Chats, die arbeiten oder auf dich warten (docs/claude.md).
  out.push(...claudeLaeuft(getClaude()?.stand.chats ?? []));
  return out;
}

/** Die Uhr für kurzText: geht alle 30 s, solange `aktiv()` gilt, und
 *  stellt sich sofort, wenn etwas zu laufen beginnt oder die Seite
 *  wieder sichtbar wird. Im Komponenten-Setup aufrufen.
 *
 *  Der Effekt hängt nur am Ja/Nein, nicht an der Liste: die ist bei
 *  jeder Drucker-Meldung neu, und ein Effekt auf sie startete das
 *  Intervall alle paar Sekunden von vorn -- die Uhr blieb beim Laden
 *  der Seite stehen, und ein Druck bis heute 11:13 hieß am Morgen
 *  „bis morgen 11:13" (Tim 17.10.2026). */
export function laeuftUhr(aktiv: () => boolean): { readonly jetzt: Date } {
  let jetzt = $state(new Date());
  const an = $derived(aktiv());
  $effect(() => {
    if (!an) return;
    const stellen = () => (jetzt = new Date());
    const sichtbar = () => {
      if (!document.hidden) stellen();
    };
    stellen();
    const iv = setInterval(stellen, 30_000);
    document.addEventListener("visibilitychange", sichtbar);
    return () => {
      clearInterval(iv);
      document.removeEventListener("visibilitychange", sichtbar);
    };
  });
  return {
    get jetzt() {
      return jetzt;
    },
  };
}

/** Der Eintrag, der auf diese Seite führt (für die Baumzeile). */
export function laeuftFuer(pfad: string): Laeuft | undefined {
  return laeuftGerade().find((l) => l.pfad === pfad);
}

/** Was in einem Bereich läuft (für den Punkt an der Aktivitätsleiste). */
export function laeuftIm(bereich: SectionId): Laeuft[] {
  return laeuftGerade().filter((l) => sectionForPath(l.pfad)?.id === bereich);
}
